import { AllPosts, ZapcapVideoStatus } from "../../types/posts";

/**
 * MB-1460: a Caption Video whose render is still running sits in Draft, so the
 * raw status alone read "Pending Approval" while the video was being made.
 * `zapCapVideoStatus` is the only signal that separates it from a post genuinely
 * waiting on the user — and from one parked in Draft by a failed caption edit,
 * which has a finished video and IS waiting on the user.
 *
 * Both the card badge and the status filter classify through here so a card can
 * never be labelled something its own filter won't match.
 */
export function isCaptionVideoRendering(
    post: Pick<AllPosts, "status" | "zapCapVideoStatus">,
): boolean {
    return (
        post.status === "VideoProcessing" ||
        post.zapCapVideoStatus === ZapcapVideoStatus.Processing
    );
}

/**
 * The status a post should be presented and filtered as — the raw status,
 * except that a still-rendering Caption Video reports as VideoProcessing
 * whatever status it is actually stored in.
 */
export function displayStatus(
    post: Pick<AllPosts, "status" | "zapCapVideoStatus">,
): string {
    return isCaptionVideoRendering(post) ? "VideoProcessing" : post.status;
}

/** The user-facing label for a display status. */
export function statusLabel(status: string): string {
    switch (status.trim().toLowerCase()) {
        case "videoprocessing":
            return "Video Processing";
        case "draft":
            return "Pending Approval";
        case "retrying":
            return "Pending Retry";
        case "approved":
            return "Approved";
        case "posted":
            return "Posted";
        case "error":
            return "Error";
        default:
            return status
                .replace(/([a-z])([A-Z])/g, "$1 $2")
                .toLowerCase()
                .replace(/\b\w/g, (c) => c.toUpperCase());
    }
}
